import { TABLE_TEXTS, formatAmount, parseGermanNumber } from '../format';

/** One field of an entry in the detail popup (AP-128). */
export interface DetailField {
  key: string;
  label: string;
  /** As shown; null or empty shows a dash. */
  value: string | null;
  /** Across the full width with its line breaks (long texts, reports). */
  long?: boolean;
  mono?: boolean;
  /** How it is edited; without it the field stays read-only in the edit mode too. */
  edit?: 'text' | 'textarea' | 'amount' | { options: readonly { value: string; label: string }[] };
  /** The stored value when it differs from what is shown (amounts, keys of a select). */
  raw?: string | number | null;
  /** Must not be emptied. */
  required?: boolean;
}

export type DetailChanges = Record<string, string | number | null>;

const stored = (f: DetailField): string | number | null => (f.raw !== undefined ? f.raw : f.value);
const amountText = (v: string | number | null): string => (typeof v === 'number' ? formatAmount(v) : '');

/** What the edit fields start with. */
export function initialValues(fields: readonly DetailField[]): Record<string, string> {
  return Object.fromEntries(fields.filter((f) => f.edit).map((f) => {
    const v = stored(f);
    return [f.key, f.edit === 'amount' ? amountText(v) : v === null ? '' : String(v)];
  }));
}

/**
 * The fields the user changed, ready to send: text trimmed, empty as null,
 * amounts as numbers. A field that cannot be read stops the save.
 */
export function formChanges(fields: readonly DetailField[], values: Record<string, string>): { changes: DetailChanges } | { error: string; key: string } {
  const changes: DetailChanges = {};
  for (const f of fields) {
    if (!f.edit) continue;
    const input = values[f.key] ?? '';
    const before = stored(f);
    if (f.edit === 'amount') {
      // Untouched stays untouched: the field shows two decimals, the value may have more.
      if (input.trim() === amountText(before)) continue;
      const n = parseGermanNumber(input);
      if (n === undefined) return { error: `${f.label}: ${TABLE_TEXTS.badNumber}`, key: f.key };
      if (n === null && f.required) return { error: `${f.label}: ${TABLE_TEXTS.required}`, key: f.key };
      if (n !== (typeof before === 'number' ? before : null)) changes[f.key] = n;
      continue;
    }
    const text = input.trim() || null;
    if (text === null && f.required) return { error: `${f.label}: ${TABLE_TEXTS.required}`, key: f.key };
    if (text !== (before === null ? null : String(before).trim() || null)) changes[f.key] = text;
  }
  return { changes };
}
